/** @csi/module/InlineController */
define([
	"dojo/_base/declare",
	"dojo/_base/event",
	"dojo/_base/lang",
	"dojo/_base/window",
	"dojo/dom-class",
	"dojo/dom-style",
	"dojo/keys",
	"dijit/popup",
	"dijit/TooltipDialog",
	"mobx",
	"csi",
	"csi/_base/DeferredTracking",
	"csi/_base/underscore",
	"csi/html",
	"csi/locale",
	"csi/message",
	"csi/reflection",
	"csi/middleware/inline/InlinePopupService",
	"csi/middleware/rollup/rollupService",
	"csi/state/rootStore",
	"csi/utility/coreUtility",
	"csi/widget/_ScopeScrollMixin",
	"csi/widget/Automation",
	"csi/widget/Tooltip",
	"csi/xml",
	"./_base",
	"./AttributeController",
	"./ValidateError"
], function(declare, event, lang, win, domClass, domStyle, keys, popup, TooltipDialog, {runInAction}, csi,
	Tracking, _, html, locale, message, reflection, {closeInlinePopup, openInlinePopup}, {validateValue},
	{browserStore}, {isDefined}, _ScopeScrollMixin, Automation, Tooltip, xml, csiHeading, AttributeController, ValidateError) {

	// TooltipDialog: reusable dialog for inline editing
	var _dialog = null;

	return declare([AttributeController, _ScopeScrollMixin], {
		/**
		 * @override
		 * Need to make sure all other actions are complete so that refNode does not get removed from DOM as part of view refresh in middle of action (C8-18411) 
		 */
		waitForTrackingComplete: true,

		// Number: number of pixels of padding and margin surrounding inline edit widget
		DIALOG_PADDING: 10,

		/** @property {boolean} bInlineEditChange - indicates if change occurred while inline editing, keeping track across multiple inline controllers using tab/enter */
		bInlineEditChange: false,

		// Boolean: by default all inline edit operations are held for publishing until user pause
		bHold: true,

		/** @property {boolean} bInlineEdit - indicates if controller was invoked from tab/enter (or creating new node) rather than from user click */
		bInlineEdit: false,

		// Boolean: controller waits for user input
		bModal: false,

		// DOMNode: HTML element used to edit heading (eventually should be widget)
		//editNode: null,

		// DOMNode: Node to wich "scroll into view" on action start
		// nodeToScrollIntoView: null,

		// flag for react editing to ignore duplicate events, or postponed events if dialog was already removed
		hasReactEditingStarted: false,

		// timeout id to track open react inline dialog process
		reactInlineDialogTimeoutId: 0,

		// inline editing latest text entered by user (example: latest select filter text)
		reactLastInput: "",

		/** @property {boolean} _dialogCreated - indicates that React inline edit dialog is created and not yet disposed */
		_dialogCreated: false,

		/** @property {Object} _editNodeStyles - styles to apply to edit node */
		_editNodeStyles: null,

		// ValidateError: error thrown in during the processing
		//_thrownException: null,

		// Number: key to process
		//_keyCode: null,

		// Boolean: indicates if SHIFT was pressed during key event
		//_keyShift: null,

		// Boolean: flag to indicate if processing is already underway for keyboard events
		//_bProcessing: false,

		// Boolean: flag to indicate if tooltip displayed for editing
		//_bTooltip: false,

		// Boolean: flag to indicate that "edit again" is started (used for onClose)
		//_bEditAgain: false,

		// Handle: handle of mouse wheel events. Used to prevent mouse wheel scrolling.
		// mouseWheelHandle: null

		/**
		 * @override
		 * Ensure the edit dialog is closed before opening confirm dialog
		 */
		confirmAction(params) {
			this._removeDialog();
			this.inherited(arguments);
		},

		getDisplayDelay: function() {
			// extension point for delay in milliseconds
			return 0;
		},

		hasPendingAction: function() {
			// summary: return true if tab or enter key used
			return !!this._keyCode;
		},

		onShowEditNode: function(/*_WidgetBase*/editNode) {
			// summary: extension point allowing editNode to be altered when displayed
		},

		preProcess: function() {
			// summary: render widget for editing on top of refNode and display any tooltip for editing
			this._createEditNode();
			this._toHtmlTooltip();
		},

		interruptProcess: function() {
			// summary: if action is in the middle of processing (timeout), then let it complete normally
			if (this._bProcessing) {
				this._bInterrupted = true;
			} else {
				this.inherited(arguments);
			}
		},

		/**
		 * Return true if a new value is required; in some cases there is custom logic to determine this per instance,
		 * so this function can be overridden to indicate so
		 * @return {boolean}
		 */
		isValueRequired() {
			return this.heading.m_bRequired;
		},

		/**
		 * create observer to watch and react on scrolls
		 * @param {HTMLElement} observerRefNode
		 **/
		positionEditNode(observerRefNode) {
			this.addIntersectionObserver(observerRefNode, () => this._scrollAway());
		},

		postProcess: function(/*Boolean*/bStatus) {
			// summary: cleanup edit control and refresh heading
			this._disconnect();
			this._removeDialog();

			// hide tooltip displayed while editing
			if (this._bTooltip) {
				Tooltip.hide(this.refNode);
			}

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Before any changes have been made to the DOM (especially in table views such as to select row), 
		 * read from DOM to get edit node styles to prevent browser from having to recalculate styles
		 */
		start: function() {
			var refNode = this.refNode;

			// reset 'refNode' if the original node is not in DOM anymore
			if (!html.inDOM(refNode)) {
				refNode = this._findRefNode();
			}

			// do this now to try to prevent as many DOM read/write switches as possible
			this.widget.scrollIntoView(this.nodeToScrollIntoView || refNode);

			var w = refNode.offsetWidth;
			var h = refNode.offsetHeight;

			// TODO: why does minWidth matter if we have width?
			var minWidth = domStyle.getComputedStyle(refNode)["min-width"];

			this._editNodeStyles = this._getEditNodeStyles(w, h, minWidth);

			this.inherited(arguments);
		},

		_createEditNode: function() {
			// summary: popup dialog to edit value
			if (this.ren.useReactInlineDialog()) {
				this._createEditNodeReact();
				return;
			}

			var self = this;
			var bFirstTime = !_dialog;
			var editNode = this.editNode;
			var refNode = this.refNode;

			domClass.add(refNode, csiHeading.CSS_EDITING);

			if (!editNode) {
				editNode = this.editNode = this._toHtmlDynamic(this._ctx, Automation.params(this._ctx.defView.getHtmlKey(), "edit", this.heading.getKey()));

				// apply CSS styles to edit node based on refNode size
				if (this._editNodeStyles) {
					domStyle.set(editNode.domNode, this._editNodeStyles);
				}

				// initialize oldValue if editing entry in container that did not previously exist; set oldValue to default value for type unless
				// attribute has some other initial value
				if ((this.oldValue == null) && (this.keyContainer != null)) {
					var defaultValue = this._fromHtmlDynamicGuarded();
					if (!defaultValue || !xml.valueIsValid(defaultValue, this.ren.type)) {
						this.oldValue = defaultValue;
					}
				}

				// add editNode class to dialog for styling
				var editNodeClass = editNode.domNode ? $(editNode.domNode).attr('class').split(' ')[0] + "-dialog" : "";

				if (_dialog) {
					// move to end of body so that popup always appear over content; there is an edge case
					// if you click fast enough where wrapper is not present, so protect against that (not sure if this
					// is a complete solution for that case, but it does protect against the error)
					if (_dialog._popupWrapper) {
						win.body().appendChild(_dialog._popupWrapper);
					}

					// replace old editNode class on dialog with new one
					var lastClass = $(_dialog.domNode).attr('class').split(' ').pop();
					if (lastClass.indexOf('-dialog') !== -1) {
						$(_dialog.domNode).toggleClass(lastClass + " " + editNodeClass);
					}

					_dialog.set("content", editNode);
				} else {
					_dialog = new TooltipDialog({
						"class": "csiInlineDialog csi-inline-editor-popup " + editNodeClass,
						"content": editNode
					});
					_dialog.startup();
				}

				// indicate that inline editing is in progress
				runInAction(() => browserStore.editingInline = true);
			}

			// re-enable widget if previously disabled
			html.setDisabled(editNode, false);

			function showPopupDialog() {
				popup.open({
					popup: _dialog,
					around: refNode,
					orient: [
						{aroundCorner: "TL", corner: "TL"},
						{aroundCorner: "TR", corner: "TR"},
						{aroundCorner: "BL", corner: "BL"},
						{aroundCorner: "BR", corner: "BR"}
					],
					onClose: () => {
						if (!self._bEditAgain) {
							self._destroyDialog(true);
							message.hide();
						}
					}
				});

				// popup by default gets zIndex of 1000+, but that means any popup dialog underlay will not hide it, so
				// reset zIndex to same as content if already in dialog (because it appears later in DOM, should always appear above content),
				// and 900 otherwise (popup dialogs start at 950 - Dialog._DialogLevelManager._beginZIndex)
				var viewDialog = self.getDialog();
				var zIndex = viewDialog ? domStyle.get(viewDialog.domNode, "zIndex") : 900;
				domStyle.set(_dialog._popupWrapper, "zIndex", zIndex);

				if (bFirstTime || $(editNode.domNode).find('.fr-element').length) {
					// focus only after dialog is built first time / froala text editor needs time to load each time
					_.delay(function() {
						if (self.isValidAction() && html.exists(editNode)) {
							editNode.focus();
						}
					}, 100);
				} else {
					editNode.focus();
				}

				// allow editNode to be altered when displayed
				self.onShowEditNode(editNode);

				self.positionEditNode(refNode);
			}

			var displayDelay = this.getDisplayDelay();

			// delay to prevent double-click events from interacting with the popup,
			// but don't delay while tabbing as focus may not work (bInlineEdit is false for keyboard events)
			if (displayDelay && !this.bInlineEdit) {
				_.delay(function() {
					if (html.exists(editNode)) {
						showPopupDialog();
					}
				}, displayDelay);
			} else {
				showPopupDialog();
			}
		},

		_editAgain: function() {
			// summary: cleanup from first edit and try again
			this.newValue = null;
			this._bProcessing = false;
			this._keyCode = null;
			this._thrownException = null;

			// if action has been interrupted, then do not edit again - just cleanup
			if (this._bInterrupted) {
				this.postProcess();
			} else {
				this._bEditAgain = true;
				this._createEditNode();
				this._bEditAgain = false;
			}

			return true;
		},

		_fromHtmlDynamic: function() {
			// summary: extract new value from edit node
			return this.ren.fromHtmlDynamic(this._ctx, this.heading, this.editNode);
		},

		_fromHtmlDynamicGuarded: function() {
			// Summary: extract new value from edit node; if validation error, catch and store for later use in _processValue
			try {
				return this._fromHtmlDynamic();
			} catch (exc) {
				if (exc instanceof ValidateError) {
					// for our errors store the exception for later use
					this._thrownException = exc;
					return null;
				} else {
					// re-throw the unknown exception
					throw exc;
				}
			}
		},

		_getEditNodeStyles: function(/*Number*/w, /*Number*/h) {
			// summary: return styles necessary to set on editNode
			return null;
		},

		/**
		 * @override
		 * Edit next heading if TAB or ENTER key used to go to next editable cell
		 */
		_onEndHandlePendingAction: function() {
			var view = this.widget;
			var bChanged = this.bInlineEditChange || this._isValueChanged();
			var bPendingAction = this.hasPendingAction();
			var refNode;

			if (bPendingAction && this._findRefNode()) {
				refNode = view.findNextHeadingNode(this.refNode, this._keyCode, this._keyShift);
			}

			// the bInlineEdit parameter will be used to restrict initial searching of data stores for drop-down attributes
			var bEdit = refNode && view.editHeading(refNode, false, {
				bInlineEdit: true,
				bInlineEditChange: bChanged
			});

			// if next heading to edit is not found, then make sure to indicate there is no pending action
			if (!bEdit && bPendingAction) {
				view.set("disabled", false);
			} else {
				// this will be used in AttributeController#_postProcessMaintainScroll function
				this.refNode = refNode;
			}

			// resize after all edits completed
			if (bChanged && (!bEdit || !bPendingAction)) {
				view.publishDomChange();
			}

			this.inherited(arguments);
		},

		_onInput: function(/*Event*/evt) {
			// summary: handle keyboard navigation
			if (!this._bProcessing && this.isValidAction(true)) {
				if (this._processKey(evt)) {
					event.stop(evt);
				}
			}
		},

		_onKeyUp: function(/*Event*/evt) {
			// summary: handle special keys - this handler is needed for
			// WebKit-based browsers which do not dispatch "onkeypress" event for ESC key
			if (!this._bProcessing && this.isValidAction(true)) {
				if (evt.keyCode == keys.ESCAPE) {
					this._onInput(evt);
				}
			}
		},

		_onProcess: function(/*Event?*/evt) {
			// summary: continue to processing value after short timeout to allow
			// other widget events to process (otherwise generates error after destroying widget)
			if (this.ren.useReactInlineDialog()) {
				this._onProcessReact(evt);
				return;
			}
			if (!this._bProcessing && this.isValidAction(true)) {
				var e = this.editNode;

				// validate widget before accepting new value
				if (e && e.validate && !e.validate()) {
					this._keyCode = this._keyShift = null;
					return;
				}

				this._bProcessing = true;
				this.newValue = this._fromHtmlDynamicGuarded();
				this._processValue();
			}
		},

		_processKey: function(/*Event*/evt) {
			// summary: handle special keys
			switch (evt.keyCode) {
				case keys.ESCAPE:
					// end action without publishing
					this._bProcessing = true;
					message.hide();
					setTimeout(lang.hitch(this, this.postProcess, null, null), 10);
					return true;

				case keys.ENTER:
					// consume (Alt/Ctrl)+Enter being pressed - only care about Enter and Shift+Enter here
					if (evt.altKey || evt.ctrlKey || evt.metaKey) {
						break;
					}

					return this._processTabOrEnter(evt);

				case keys.TAB:
					return this._processTabOrEnter(evt);
			}

			return false;
		},

		_processTabOrEnter: function(/*Event*/evt) {
			// summary: handle keypress of tab or enter if widget is valid
			this._keyCode = evt.keyCode;
			this._keyShift = evt.shiftKey;
			this._onProcess(evt);
			return true;
		},

		_processValue: function() {
			// summary: validate value and either go to processing or revert back to editing
			var self = this;

			Tracking.restart();

			if (this._thrownException) {
				// notify user of invalid value and edit value again
				message.error(this._thrownException.message);
				this._editAgain();
			} else if (this._isValueChanged()) {
				message.hide();

				this.when(this.validate(),
					function() {
						// disable widget while processing
						html.setDisabled(self.editNode, true);
						self.process();
					},
					function(err) {
						// notify user of invalid value and edit value again
						message.error(err.message);
						self._editAgain();
					}
				);
			} else {
				// nothing changed, so just refresh
				message.hide();
				this.postProcess();
			}
		},

		/**
		 * On scroll away save editor content except for control with opened drop down
		 */
		_scrollAway() {
			if (this.editNode || this.ren.useReactInlineDialog()) {
				this._removeDialog();
			}
		},

		/**
		 * close popup dialog
		 */
		_removeDialog: function() {
			if (this.ren.useReactInlineDialog()) {
				this._removeReactDialog();
			} else if (this.editNode) {
				popup.close(_dialog);
			}

			// indicate that inline editing is done
			runInAction(() => browserStore.editingInline = false);
		},

		/**
		 * destroy popup dialog
		 */
		_destroyDialog: function() {
			if (this.editNode) {
				this.removeIntersectionObserver();
				this.editNode.destroy();
				this.editNode = null;
				this._disconnect();
				domClass.remove(this.refNode, csiHeading.CSS_EDITING);
			}

			// indicate that inline editing is done
			runInAction(() => browserStore.editingInline = false);
		},

		_toHtmlDynamic: function(/*Context*/ctx, /*Object*/params) {
			// summary: create widget for editing heading
			var editNode = this.ren.toHtmlDynamic(ctx, this.heading, this.refNode, params);

			// set search on focus property when invoking from mouse click; don't want to search on focus when tabbing through table
			editNode.bOpenOnFocus = !this.bInlineEdit;

			return editNode;
		},

		_toHtmlTooltip: function() {
			// summary: create tooltip content to aid editing
			const {
				editNode,
				heading,
				refNode,
				ren,
				_ctx,
			} = this;

			const divTooltip = ren.toTooltipEdit(_ctx, heading, refNode, editNode);
			if (divTooltip) {
				this._bTooltip = true;
				Tooltip.hide();

				// looks like some dijit widgets force hiding of tooltip as well, so wait for that to happen before popping up tooltip
				_.delay(() => {
					Tooltip.applyMuiStyling(_ctx.view.useMuiTooltipStyling);
					Tooltip.show(divTooltip, refNode, ["above", "after", "before"], false, "", true);
				}, 10);
			}
		},

		/**
		 * Throw error to signal an invalid value
		 */
		_validateForm: function() {
			var h = this.heading;
			var node = this.getNode();
			var rca = reflection.getAttribute(node, h.m_id);
			var type = this._getValueType(node, h.m_id);
			var base = xml.attributeTypeGetBase(type);
			var format = h.m_format;

			// get the format from the object model if it's not defined on the heading
			if (rca && !format) {
				format = rca.getFormat();
			}

			// any errors will be thrown as exceptions
			if (base && this.keyContainer === null) {
				// check attribute as container type
				if (this.isValueRequired() && !xml.valueIsValid(this.newValue, type)) {
					throw new Error(locale.resource("ValidateRequired"));
				}
			} else {
				csiHeading.validateValue(this.newValue, base || type, format, this.isValueRequired());
			}
			this.ren.validateValue(this._ctx, this.heading, this.newValue);

			// if attribute has validation expression, try executing locally before trying on server
			if (rca && rca.getValidation()) {
				var bValid = validateValue(this.newValue, rca.getValidation());
				var typeNode = rca.getOwner().getType();

				if (bValid === null) {
					// client cannot execute expression, so force publishing to server immediately
					this.m_writer.setCannotCache();
				} else if (!bValid) {
					throw new Error(locale.getValidationError(typeNode, h.m_id));
				}
			}
		},

		/** React */

		/**
		 * return value of inline editing from either react or old style editor
		 */
		getInlineEditValue: function() {
			return this.ren.useReactInlineDialog() ? this.newValue.toString() : this.editNode?.get("value");
		},

		/**
		 * Retrieve values which are already in memory to create placeholder in inline control
		 * @returns {SelectOptions}
		 */
		getReactPlaceholderOptions() {
			const {heading, ren} = this;
			return ren.getReactPlaceholderOptions(this, this.getContext(), heading);
		},

		/**
		 * ignore undefined/null
		 */
		setNewValueOnChange(newValue) {
			if (isDefined(newValue)) {
				this.newValue = newValue;
			}
		},

		/**
		 * create react control/component
		 */
		_createEditNodeReact() {
			// reset hasReactEditingStarted to allow edit component's events
			// if validation failed and we "edit again" - also reset it to false to allow editing events
			this.hasReactEditingStarted = false;

			if (this._bEditAgain) {
				return;
			}
			const {refNode, ren} = this;

			this.newValue = this.oldValue;

			const startProcessing = () => {
				// ignore duplicate events, or postponed events if dialog was already removed
				if (this.hasReactEditingStarted || !this._dialogCreated || !this.isValidAction()) {
					return false;
				}

				this.hasReactEditingStarted = true;
				return true;
			}

			const onChange = (ev, newValue) => {
				if (ev.key === "Escape") {
					onCancel();
					return;
				}

				if (startProcessing()) {
					this.setNewValueOnChange(newValue);

					if ((ev.key === "Enter") || (ev.key === "Tab")) {
						this._processKey(ev);
					} else {
						this._processValue();
					}
				}
			};

			const onCancel = () => {
				if (startProcessing()) {
					this.postProcess();
				}
			}

			const onLastInputChange = (value) => {
				this.reactLastInput = value;
			}

			this.positionEditNode(refNode);

			const getReactOptions = (store, query, queryOptions) => {
				const res = ren.getReactControlOptions(store, this, query, queryOptions);
				// old value may change as a result of aggregate store work 
				// see aggregateController->_onAggregateStoreResponse();
				// newValue must use this updated value
				this.newValue = this.oldValue;
				return res;
			}

			// create timeout and delay inline dialog creation. Since all inline dialogs share the same ReactPortal slot, 
			// we have to ensure that previous REACT dialog get properly destroyed before creating next one
			// if another timeout is already pending - cancel it, as it's action probably already aborted
			if (this.reactInlineDialogTimeoutId) {
				clearTimeout(this.reactInlineDialogTimeoutId);
				this.reactInlineDialogTimeoutId = null;
			}
			this.reactInlineDialogTimeoutId = setTimeout(() => {
				this.reactInlineDialogTimeoutId = null;
				const bOpen = !this.bInlineEdit;
				openInlinePopup(
					onCancel,
					onChange,
					this._removeReactDialog.bind(this),
					onLastInputChange,
					refNode,
					this.heading,
					this.ren,
					getReactOptions,
					{...this.getReactPlaceholderOptions()},
					this.isValueRequired(),
					this.ren.getReactControlType(this.getContext(), this.heading),
					bOpen,
				);
				this._dialogCreated = true;

				// indicate that inline editing is in progress
				runInAction(() => browserStore.editingInline = true);
			});
		},

		/**
		 * create list of select actions for React Selects
		 * @param {*} a - action to use for popup view 
		 * @param {Names} store - existing renderer store
		 * @return {SelectAction} list of select actions for React Selects 
		 */
		getSelectActions(a, store) {
			return [];
		},

		/**
		 * onProcess for react - do not try to extract value from editNode, use already defined newValue instead
		 */
		_onProcessReact(evt) {
			if (!this._bProcessing && this.isValidAction(true)) {
				this._bProcessing = true;
				this._processValue();
			}
		},

		/**
		 * @override
		 */
		_removeReactDialog() {
			// removeDialog may trigger on scrollAway and other delayed/postponed events. The nature of this function is call to
			// "routerStore.removeDialog", which will remove any dialog, if called. Even other dialog, not created in this
			// instance of this controller. So, avoid calling this multiple times, make it only once
			if (!this._dialogCreated) {
				return;
			}
			closeInlinePopup();
			this._dialogCreated = false;
		},

	});
});